/* ═══════════════════════════════════════════════════════════════
   Project panel prefs — pure order/collapse state for the Project
   details panel's three sections (Details, Phase, Design brief).
   Persisted per-user cross-device via `usePref` (ProjectPanel.tsx),
   key 'projectPanel'. Extracted so the reducer-style transitions are
   unit-testable without React or the DOM.
   ═══════════════════════════════════════════════════════════════ */

export type PanelSectionId = 'details' | 'phase' | 'brief';

export interface ProjectPanelPrefs {
  order: PanelSectionId[];
  collapsed: Record<PanelSectionId, boolean>;
}

export const DEFAULT_PROJECT_PANEL_PREFS: ProjectPanelPrefs = {
  order: ['details', 'phase', 'brief'],
  collapsed: { details: false, phase: false, brief: false },
};

/** Swaps `id` with its neighbor in `dir`. No-op (returns the SAME array
 *  reference) at either end so callers can disable the button on an
 *  unchanged result without a second comparison. */
export function movePanelSection(
  order: PanelSectionId[],
  id: PanelSectionId,
  dir: 'up' | 'down'
): PanelSectionId[] {
  const idx = order.indexOf(id);
  if (idx === -1) return order;
  const target = dir === 'up' ? idx - 1 : idx + 1;
  if (target < 0 || target >= order.length) return order;
  const next = order.slice();
  [next[idx], next[target]] = [next[target], next[idx]];
  return next;
}

/** Flips one section's collapsed flag. */
export function togglePanelSection(
  collapsed: Record<PanelSectionId, boolean>,
  id: PanelSectionId
): Record<PanelSectionId, boolean> {
  return { ...collapsed, [id]: !collapsed[id] };
}
